import React, { FC, useEffect, useState } from 'react'
import {
  Heading,
  IconButton,
  Tooltip,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Button,
  Box,
} from '@chakra-ui/react'
import { styled } from 'styled-components'
import { FaRegTrashAlt } from 'react-icons/fa'
import { FiArchive, FiEdit2, FiShare2 } from 'react-icons/fi'
import { PiArrowUUpLeftBold } from 'react-icons/pi'
import { MdMoreVert, MdOutlineGroups, MdArrowOutward, MdOpenInNew } from 'react-icons/md'

const TitleContainer = styled.div`
  position: relative;
  width: 100%;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;

  h1 {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
  }

  .TitleEditButton {
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    .TitleEditButton {
      pointer-events: all;
      opacity: 1;
    }
  }
`

const ChakraMenuListContainer = styled.div`
  display: flex;

  .chakra-menu-list {
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    background: #fff;
    box-shadow:
      0px 0px 4px 0px rgba(0, 0, 0, 0.04),
      0px 4px 8px 0px rgba(0, 0, 0, 0.06);
    display: flex;
    min-width: 154px;
    width: 154px;
    padding: 8px;
    gap: 4px;
  }

  .chakra-menu-item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border-radius: 10px;

    p {
      color: #585858;
      font-size: 12px;
      font-weight: 500;
      line-height: 16px; /* 133.333% */
      white-space: nowrap;
    }

    svg {
      margin-right: 8px;
      color: #585858;
    }

    &:hover {
      background: #ebebeb;
      text-decoration: none;

      svg {
        color: #585858;
      }
    }

    &:focus {
      background: #ebebeb;
      text-decoration: none;
    }
  }
`

interface Props {
  title: string
  isArchived: boolean
  isShared: boolean
  isOpenDisabled: boolean
  isCloseDisabled: boolean
  shouldHideActionIcons: boolean
  handleClickDeleteIcon: (event: React.MouseEvent<Element, MouseEvent>) => void
  handleClickArchiveIcon: (event: React.MouseEvent<Element, MouseEvent>) => void
  handleClickUnarchiveIcon: (event: React.MouseEvent<Element, MouseEvent>) => void
  handleClickOpenIcon: (event: React.MouseEvent<Element, MouseEvent>, newWindow: boolean) => void
  handleClickCloseIcon: (event: React.MouseEvent<Element, MouseEvent>) => void
  handleClickRenameIcon: (event: React.MouseEvent<Element, MouseEvent>) => void
  handleClickShareIcon: (event: React.MouseEvent<Element, MouseEvent>) => void
  onMenuOpen: () => void
  onMenuClose: () => void
}

const ProjectDetailStaticTitle: FC<Props> = (props) => {
  const {
    title,
    isArchived,
    isShared,
    isOpenDisabled,
    isCloseDisabled,
    handleClickDeleteIcon,
    handleClickArchiveIcon,
    handleClickUnarchiveIcon,
    handleClickOpenIcon,
    handleClickCloseIcon,
    handleClickRenameIcon,
    handleClickShareIcon,
    shouldHideActionIcons,
    onMenuOpen,
    onMenuClose,
  } = props
  const [wasMenuJustClosed, setWasMenuJustClosed] = useState<boolean>(false)

  const onMenuCloseWrapper = () => {
    setWasMenuJustClosed(true) //Fixes bug where tooltip reappears after closing menu
    onMenuClose()
  }

  useEffect(() => {
    if (wasMenuJustClosed) {
      setTimeout(() => {
        setWasMenuJustClosed(false)
      }, 100)
    }
  }, [wasMenuJustClosed])

  return (
    <TitleContainer>
      <Flex alignItems="center">
        <Heading as="h1" className="is-ellipsis-1">
          {title}
        </Heading>
        <Tooltip placement="top" label="Rename folder">
          <span className="TitleEditButton hover-show clickable" onClick={handleClickRenameIcon}>
            <FiEdit2 color="#585858" size={16} style={{ marginLeft: '5px', flexShrink: 0 }} />
          </span>
        </Tooltip>
        <Flex ml="8px" gap="8px" alignItems="center">
          {isArchived && (
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="24px"
              p="4px 12px"
              fontWeight={500}
              fontSize="12px"
              lineHeight="16px"
              color="#585858"
              bg="#E0E0E0"
            >
              Archived
            </Flex>
          )}

          {isShared && (
            <Flex
              alignItems="center"
              justifyContent="center"
              borderRadius="8px"
              p="4px 8px"
              gap="6px"
              fontWeight={500}
              fontSize="12px"
              lineHeight="16px"
              color="#585858"
              bg="#E0E0E0"
            >
              <MdOutlineGroups />
              Shared
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex
        height="22px"
        flex="0 0 auto"
        alignItems="center"
        marginLeft="8px"
        opacity={shouldHideActionIcons ? 0 : 1}
        pointerEvents={shouldHideActionIcons ? 'none' : 'auto'}
        transition="opacity 0.2s"
      >
        {!isArchived && (
          <Box className="flex-center" height="100%" cursor="pointer">
            <Tooltip
              placement="top"
              label={isOpenDisabled ? 'All tabs open' : 'Open all in new window'}
            >
              <IconButton
                variant="outline"
                border="none"
                aria-label={`Open in new window`}
                as="a"
                icon={<MdOpenInNew color={isOpenDisabled ? '#585858' : 'black'} />}
                onClick={(e) => {
                  handleClickOpenIcon(e, true)
                }}
                height="24px"
                width="24px"
                isDisabled={isOpenDisabled}
                minWidth="24px"
                tabIndex={-1}
                _hover={{ background: 'inherit' }}
              />
            </Tooltip>
            <Tooltip placement="top" label={isOpenDisabled ? 'All tabs open' : 'Open all'}>
              <IconButton
                variant="outline"
                border="none"
                aria-label={`Open all tabs in current window`}
                as="a"
                icon={<MdArrowOutward color={isOpenDisabled ? '#585858' : 'black'} />}
                onClick={(e) => {
                  handleClickOpenIcon(e, false)
                }}
                height="24px"
                width="24px"
                isDisabled={isOpenDisabled}
                minWidth="24px"
                tabIndex={-1}
                _hover={{ background: 'inherit' }}
              />
            </Tooltip>
            <Tooltip placement="top" label={isCloseDisabled ? 'No open tabs' : 'Close all'}>
              <span>
                <IconButton
                  variant="outline"
                  border="none"
                  aria-label={`Close all tabs`}
                  as="a"
                  icon={
                    <MdArrowOutward
                      style={{ transform: 'rotate(180deg)' }}
                      color={isCloseDisabled ? '#585858' : 'black'}
                    />
                  }
                  onClick={handleClickCloseIcon}
                  height="24px"
                  width="24px"
                  isDisabled={isCloseDisabled}
                  minWidth="24px"
                  tabIndex={-1}
                  _hover={{ background: 'inherit' }}
                />
              </span>
            </Tooltip>
            <Button
              variant="outline"
              border="none"
              borderRadius="16px"
              aria-label={`Share folder`}
              leftIcon={<FiShare2 color="white" />}
              onClick={handleClickShareIcon}
              height="28px"
              mr="8px"
              ml="16px"
              px="12px"
              py="4px"
              fontSize="12px"
              fontWeight={500}
              background="black"
              color="white"
              tabIndex={-1}
              _hover={{
                color: 'white',
                background: '#585858',
              }}
            >
              Share
            </Button>
          </Box>
        )}

        <Menu onOpen={onMenuOpen} onClose={onMenuCloseWrapper}>
          <Box className="flex-center" height="100%" cursor="pointer" marginLeft="2px">
            <Tooltip isDisabled={wasMenuJustClosed} label={`More`} placement="top">
              <MenuButton
                as={IconButton}
                variant="outline"
                border="none"
                aria-label={`See more options`}
                icon={<MdMoreVert color="#585858" />}
                onClick={(e) => e.stopPropagation()}
                height="24px"
                width="24px"
                minWidth="24px"
                tabIndex={-1}
                _hover={{
                  background: 'inherit',
                }}
              />
            </Tooltip>
          </Box>
          <Portal>
            <ChakraMenuListContainer>
              <MenuList className="chakra-menu-list">
                {!isArchived && (
                  <MenuItem className="chakra-menu-item" onClick={handleClickArchiveIcon}>
                    <FiArchive />
                    <p>Archive folder</p>
                  </MenuItem>
                )}
                {isArchived && (
                  <>
                    <MenuItem className="chakra-menu-item" onClick={handleClickUnarchiveIcon}>
                      <PiArrowUUpLeftBold />
                      <p>Restore folder</p>
                    </MenuItem>
                    <MenuItem className="chakra-menu-item" onClick={handleClickDeleteIcon}>
                      <FaRegTrashAlt />
                      <p>Delete folder</p>
                    </MenuItem>
                  </>
                )}
              </MenuList>
            </ChakraMenuListContainer>
          </Portal>
        </Menu>
      </Flex>
    </TitleContainer>
  )
}

export default ProjectDetailStaticTitle
